/**
 * More Tadika games: Pecah Belon (maths), Asingkan (sains), Eja Perkataan and Padan Kad.
 * Pure content and question generators. Pictures are the drawn art ids under apps/web/public/art (never emoji),
 * and every spelled word is a Jom Baca word, so its audio clip already exists.
 */
import { kiraOptions } from "./tadikaGames.js";

type Rnd = () => number;
export function shuffled<T>(a: readonly T[], rnd: Rnd = Math.random): T[] {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [b[i], b[j]] = [b[j]!, b[i]!]; }
  return b;
}

/* ── Pecah Belon (maths within 10) ───────────────────────────────────────── */
export type BelonMode = "tambah" | "tolak" | "campur";
export type BelonQuestion = { text: string; spoken: string; answer: number; options: number[] };

/** Add (total ≤ 10) or take away (never below 1), with four different answers 1–10 including the right one. */
export function belonQuestion(mode: BelonMode, rnd: Rnd = Math.random, lang: "ms" | "en" = "ms"): BelonQuestion {
  const op = mode === "campur" ? (rnd() < 0.5 ? "tambah" : "tolak") : mode;
  if (op === "tambah") {
    const a = 1 + Math.floor(rnd() * 8);
    const b = 1 + Math.floor(rnd() * (10 - a));
    return { text: `${a} + ${b}`, spoken: lang === "en" ? `What is ${a} plus ${b}?` : `Berapa ${a} tambah ${b}?`, answer: a + b, options: kiraOptions(a + b, 10, rnd) };
  }
  const a = 2 + Math.floor(rnd() * 9);
  const b = 1 + Math.floor(rnd() * (a - 1));
  return { text: `${a} − ${b}`, spoken: lang === "en" ? `What is ${a} minus ${b}?` : `Berapa ${a} tolak ${b}?`, answer: a - b, options: kiraOptions(a - b, 10, rnd) };
}

/* ── Asingkan (sains) ────────────────────────────────────────────────────── */
export type SortItem = { art: string; name: string; to: number };
export type SortTopic = { id: string; title: string; buckets: { name: string; tone: string }[]; items: SortItem[] };

const A = (art: string, name: string, to: number): SortItem => ({ art, name, to });
export const SORT_TOPICS: SortTopic[] = [
  {
    id: "haiwan-tumbuhan", title: "Haiwan atau Tumbuhan?",
    buckets: [{ name: "Haiwan", tone: "from-amber-300 to-orange-400" }, { name: "Tumbuhan", tone: "from-emerald-400 to-green-500" }],
    items: [
      A("haiwan/ayam", "Ayam", 0), A("haiwan/kucing", "Kucing", 0), A("haiwan/gajah", "Gajah", 0), A("haiwan/ikan", "Ikan", 0), A("haiwan/katak", "Katak", 0),
      A("haiwan/burung", "Burung", 0), A("haiwan/lembu", "Lembu", 0), A("haiwan/harimau", "Harimau", 0), A("haiwan/itik", "Itik", 0), A("haiwan/arnab", "Arnab", 0),
      A("alam/pokok", "Pokok", 1), A("alam/bunga", "Bunga", 1), A("baca/padi", "Padi", 1), A("baca/jagung", "Jagung", 1), A("baca/sawi", "Sawi", 1),
    ],
  },
  {
    id: "hidup", title: "Hidup atau Bukan Hidup?",
    buckets: [{ name: "Hidup", tone: "from-emerald-400 to-teal-500" }, { name: "Bukan hidup", tone: "from-sky-400 to-indigo-500" }],
    items: [
      A("haiwan/kucing", "Kucing", 0), A("haiwan/ikan", "Ikan", 0), A("haiwan/ayam", "Ayam", 0), A("haiwan/burung", "Burung", 0), A("alam/pokok", "Pokok", 0), A("alam/bunga", "Bunga", 0),
      A("baca/batu", "Batu", 1), A("baca/kerusi", "Kerusi", 1), A("baca/kereta", "Kereta", 1), A("baca/buku", "Buku", 1), A("baca/bola", "Bola", 1), A("baca/lampu", "Lampu", 1), A("baca/pintu", "Pintu", 1),
    ],
  },
  {
    id: "apung", title: "Terapung atau Tenggelam?",
    buckets: [{ name: "Terapung", tone: "from-sky-400 to-cyan-500" }, { name: "Tenggelam", tone: "from-violet-400 to-indigo-600" }],
    items: [
      A("kenderaan/bot", "Bot", 0), A("kenderaan/kapal", "Kapal", 0), A("baca/kayu", "Kayu", 0), A("nombor/bola", "Bola", 0), A("nombor/belon", "Belon", 0), A("baca/itik", "Itik", 0),
      A("baca/batu", "Batu", 1), A("baca/paku", "Paku", 1), A("baca/syiling", "Syiling", 1), A("baca/lampu", "Lampu", 1),
    ],
  },
];
export const SORT_ROUND = 10;

/** One round: up to SORT_ROUND items, shuffled, with both buckets always present. */
export function sortRound(topic: SortTopic, rnd: Rnd = Math.random): SortItem[] {
  const mix = shuffled(topic.items, rnd);
  const out = mix.slice(0, SORT_ROUND);
  for (let b = 0; b < topic.buckets.length; b++) {
    if (!out.some((x) => x.to === b)) out[out.length - 1] = mix.find((x) => x.to === b)!;
  }
  return shuffled(out, rnd);
}

/** Pancaindera: which body part do we use? */
export type Deria = { part: string; art: string; question: string };
export const PANCAINDERA: Deria[] = [
  { part: "Mata", art: "badan/mata", question: "Kita melihat dengan apa?" },
  { part: "Telinga", art: "badan/telinga", question: "Kita mendengar dengan apa?" },
  { part: "Hidung", art: "badan/hidung", question: "Kita menghidu bau dengan apa?" },
  { part: "Mulut", art: "badan/mulut", question: "Kita merasa makanan dengan apa?" },
  { part: "Tangan", art: "badan/tangan", question: "Kita memegang benda dengan apa?" },
];
export function pancaOptions(answer: Deria, rnd: Rnd = Math.random, list: Deria[] = PANCAINDERA): Deria[] {
  return shuffled([answer, ...shuffled(list.filter((d) => d.part !== answer.part), rnd).slice(0, 3)], rnd);
}

/* ── Eja Perkataan & Padan Kad ───────────────────────────────────────────── */
/** Picture words (all Jom Baca words, so they have audio and art). */
export const EJA_WORDS = [
  "bola", "buku", "baju", "kuda", "roti", "susu", "topi", "ikan", "ayam", "itik", "katak", "kucing", "bunga", "pisang", "rumah",
  "kasut", "payung", "nanas", "lampu", "tikus", "ular", "pintu", "kerusi", "kereta", "telur", "pokok",
];
export const EJA_ROUND = 8;

/** The letters of a word in a different order (never the word itself unless every letter is the same). */
export function scramble(word: string, rnd: Rnd = Math.random): string[] {
  const letters = [...word];
  if (new Set(letters).size < 2) return letters;
  let out = shuffled(letters, rnd);
  for (let guard = 0; guard < 50 && out.join("") === word; guard++) out = shuffled(letters, rnd);
  if (out.join("") === word) out = [...letters.slice(1), letters[0]!];
  return out;
}

export type PadanMode = "huruf" | "gambar";
export type PadanCard = { id: string; pair: string; kind: "text" | "art"; text: string; art?: string };
export const PADAN_PAIRS = 6;
const PADAN_LETTERS = "ABCDEFGHIJKLMNOPRSTUW".split("");

/** 6 pairs: A↔a for letters, or a picture↔its word. Cards are shuffled. */
export function padanDeck(mode: PadanMode, rnd: Rnd = Math.random, opt?: { letters?: string[]; words?: { w: string; art: string }[] }): PadanCard[] {
  const cards: PadanCard[] = [];
  if (mode === "huruf") {
    for (const L of shuffled(opt?.letters ?? PADAN_LETTERS, rnd).slice(0, PADAN_PAIRS)) {
      cards.push({ id: `${L}-b`, pair: L, kind: "text", text: L }, { id: `${L}-k`, pair: L, kind: "text", text: L.toLowerCase() });
    }
  } else {
    for (const { w, art } of shuffled(opt?.words ?? EJA_WORDS.map((x) => ({ w: x, art: `baca/${x}` })), rnd).slice(0, PADAN_PAIRS)) {
      cards.push({ id: `${w}-g`, pair: w, kind: "art", text: w, art }, { id: `${w}-t`, pair: w, kind: "text", text: w });
    }
  }
  return shuffled(cards, rnd);
}
